/**
 * Lightweight stale-while-TTL cache for API calls.
 *
 * - Returns cached data instantly if fetched within `ttlMs`.
 * - Deduplicates concurrent requests to the same key (second caller awaits the first).
 * - On failure the entry is not written, so the next call tries the network again.
 */

type Entry<T> = { data: T; at: number };

const _store = new Map<string, Entry<unknown>>();
const _inflight = new Map<string, Promise<unknown>>();

export function withCache<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const hit = _store.get(key) as Entry<T> | undefined;

  if (hit && now - hit.at < ttlMs) {
    return Promise.resolve(hit.data);
  }

  const pending = _inflight.get(key) as Promise<T> | undefined;
  if (pending) return pending;

  const p = fetcher()
    .then((data) => {
      _store.set(key, { data, at: Date.now() });
      _inflight.delete(key);
      return data;
    })
    .catch((err: unknown) => {
      _inflight.delete(key);
      throw err;
    });

  _inflight.set(key, p as Promise<unknown>);
  return p;
}

export function bustCache(...keys: string[]): void {
  if (keys.length === 0) {
    _store.clear();
    _inflight.clear();
  } else {
    for (const k of keys) {
      _store.delete(k);
      _inflight.delete(k);
    }
  }
}
